import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useAuthStore } from "./auth/store";
const drift = keyframes`
  0% {
    transform: translate3d(-8px, -10px, 0) scale(1);
    opacity: 0.75;
  }
  50% {
    transform: translate3d(10px, 8px, 0) scale(1.06);
    opacity: 1;
  }
  100% {
    transform: translate3d(-8px, -10px, 0) scale(1);
    opacity: 0.75;
  }
`;

const glow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.25);
  }
  50% {
    box-shadow: 0 0 0 18px rgba(37, 99, 235, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const SplashScreen = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: linear-gradient(155deg, #f8fbff 0%, #eef4ff 48%, #fef3f2 100%);
`;

const SplashCard = styled.div`
  position: relative;
  width: min(420px, calc(100% - 3rem));
  border-radius: 24px;
  padding: 2.25rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(8px);
  box-shadow: 0 20px 55px rgba(15, 23, 42, 0.12);
`;

const ColorOrb = styled.div<{ $top?: string; $left?: string; $right?: string; $delay?: string }>`
  position: absolute;
  width: 110px;
  height: 110px;
  border-radius: 999px;
  filter: blur(1px);
  background: radial-gradient(circle at 32% 32%, #3b82f6 0%, #8b5cf6 62%, #f97316 100%);
  top: ${(props) => props.$top ?? "auto"};
  left: ${(props) => props.$left ?? "auto"};
  right: ${(props) => props.$right ?? "auto"};
  animation: ${drift} 4.2s ease-in-out infinite;
  animation-delay: ${(props) => props.$delay ?? "0s"};
  opacity: 0.8;
`;

const LogoWrap = styled.div`
  position: relative;
  width: 94px;
  height: 94px;
  margin: 0 auto 1.1rem;
  border-radius: 18px;
  background: #ffffff;
  display: grid;
  place-items: center;
  animation: ${glow} 1.8s ease-out infinite;
`;

const LogoImage = styled.img`
  width: 68px;
  height: 68px;
`;

const Heading = styled.h1`
  margin: 0;
  font-size: 1.35rem;
  color: #0f172a;
`;

const Subheading = styled.p`
  margin: 0.7rem 0 1.4rem;
  color: #475569;
  font-size: 0.95rem;
`;

const LoadingBar = styled.div`
  height: 7px;
  border-radius: 999px;
  overflow: hidden;
  background-color: #dbeafe;

  &::before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    background: linear-gradient(120deg, #60a5fa 0%, #2563eb 38%, #f97316 76%, #60a5fa 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.25s linear infinite;
  }
`;

const logoUrl = new URL("./assets/logo.png", import.meta.url).toString();

export function Bootstrap({ children }: { children: React.ReactNode }) {
  const [isAuthCheckComplete, setIsAuthCheckComplete] = React.useState(false);
  const {
    actions: { checkAuth },
  } = useAuthStore();

  useEffect(() => {
    let isMounted = true;

    const runBootstrap = async () => {
      const minimumSplashTime = new Promise((resolve) => setTimeout(resolve, 900));
      await Promise.all([checkAuth(), minimumSplashTime]);

      if (isMounted) {
        setIsAuthCheckComplete(true);
      }
    };

    runBootstrap();

    return () => {
      isMounted = false;
    };
  }, [checkAuth]);

  if (!isAuthCheckComplete) {
    return (
      <SplashScreen>
        <SplashCard>
          <ColorOrb $top="-24px" $left="-26px" />
          <ColorOrb $top="-16px" $right="-20px" $delay="-1.5s" />
          <LogoWrap>
            <LogoImage src={logoUrl} alt="LaceAI" />
          </LogoWrap>
          <Heading>LaceAI</Heading>
          <Subheading>Warming up your next Garmin-ready session...</Subheading>
          <LoadingBar aria-hidden="true" />
        </SplashCard>
      </SplashScreen>
    );
  }

  return <>{children}</>;
}
